import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchTLSConfig, uploadTLSConfig } from "../../../api";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((t - Date.now()) / (24 * 60 * 60 * 1000));
}

function CopyableText({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button type="button" className="gwLicenseCopyBtn" onClick={onCopy}>
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// CertInputRow lets the admin EITHER pick a file OR paste PEM text.
// Toggle persists per-row so cert/key can be supplied independently
// (e.g. cert from file, key from password manager paste).
//
// onChange is called with a File (or null) — when paste-mode is active
// and the textarea has content, the parent posts the text as a Blob/File
// using the same multipart upload path. This matches Ignition's
// "upload file or paste contents" pattern.
function CertInputRow({ label, name, file, text, onFile, onText, mode, onMode }) {
  const inputRef = useRef(null);
  return (
    <div className="gwTlsInputBlock">
      <div className="gwTlsInputHeader">
        <div className="gwTlsFileLabel">{label}</div>
        <div className="gwTlsModeTabs" role="tablist" aria-label={`${label} input mode`}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "file"}
            className={`gwTlsModeTab ${mode === "file" ? "is-active" : ""}`}
            onClick={() => onMode("file")}
          >
            Upload file
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "paste"}
            className={`gwTlsModeTab ${mode === "paste" ? "is-active" : ""}`}
            onClick={() => onMode("paste")}
          >
            Paste content
          </button>
        </div>
      </div>

      {mode === "file" ? (
        <div className="gwTlsFileRow">
          <input
            ref={inputRef}
            type="file"
            accept=".pem,.crt,.key,.cer"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = "";
            }}
          />
          <div className="gwTlsFileName">
            {file ? file.name : <span className="gwMuted">No file selected</span>}
          </div>
          <button
            type="button"
            className="gwLicenseCopyBtn"
            onClick={() => inputRef.current?.click()}
          >
            {file ? "Replace" : "Choose file"}
          </button>
        </div>
      ) : (
        <textarea
          className="gwTlsPasteArea"
          name={name}
          placeholder={
            name === "key"
              ? "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
              : "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
          }
          spellCheck={false}
          autoComplete="off"
          value={text}
          onChange={(e) => onText(e.target.value)}
          rows={8}
        />
      )}
    </div>
  );
}

// Convert a paste-mode textarea string into a File the multipart upload
// path expects. Lightweight client-side trim — the backend re-validates
// the PEM block and the cert/key pairing, so trimming is just to avoid
// sending unnecessary whitespace bytes.
function pemTextToFile(text, filename) {
  const trimmed = (text || "").trim();
  if (!trimmed) return null;
  return new File([trimmed + "\n"], filename, { type: "application/x-pem-file" });
}

export function PlatformTLSPage() {
  const isAdmin = usePermission("admin");
  const notify = useNotify();
  const [state, setState] = useState({ loading: true });

  const [certMode, setCertMode] = useState("file");
  const [keyMode, setKeyMode] = useState("file");
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [certText, setCertText] = useState("");
  const [keyText, setKeyText] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchTLSConfig();
      if (data === null) {
        setState({ loading: false, available: false });
      } else {
        setState({ loading: false, available: true, ...data });
      }
    } catch (err) {
      setState({ loading: false, available: true, error: err?.message });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onUpload = useCallback(
    async (e) => {
      e.preventDefault();
      setUploadError(null);

      const certPayload =
        certMode === "file" ? certFile : pemTextToFile(certText, "cert.pem");
      const keyPayload =
        keyMode === "file" ? keyFile : pemTextToFile(keyText, "key.pem");

      if (!certPayload || !keyPayload) {
        setUploadError(
          "Provide both a certificate and a private key (file or pasted PEM)."
        );
        return;
      }

      setUploading(true);
      try {
        const data = await uploadTLSConfig(certPayload, keyPayload);
        setState({ loading: false, available: true, ...data });
        setCertFile(null);
        setKeyFile(null);
        setCertText("");
        setKeyText("");
        notify?.({
          type: "success",
          title: "Certificate active",
          message: "New TLS handshakes use it immediately — no restart needed.",
        });
      } catch (err) {
        setUploadError(err?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [certMode, keyMode, certFile, keyFile, certText, keyText, notify]
  );

  if (state.loading) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">Web Server / TLS</div>
              <div className="gwPageSubtitle">Loading…</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!state.available) {
    // Cloud SaaS: TLS is terminated at the load balancer; the nav filter
    // hides this page. Defensive bail-out in case it renders anyway.
    return null;
  }

  const cfg = state.config;
  const expDays = cfg ? daysUntil(cfg.cert_not_after) : null;
  const expiryClass =
    expDays === null
      ? ""
      : expDays < 0
      ? "gwTlsExpiry-expired"
      : expDays < 30
      ? "gwTlsExpiry-soon"
      : "gwTlsExpiry-ok";

  const hasInput =
    (certMode === "file" ? !!certFile : certText.trim().length > 0) &&
    (keyMode === "file" ? !!keyFile : keyText.trim().length > 0);

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Web Server / TLS</div>
            <div className="gwPageSubtitle">
              Upload an HTTPS certificate so the gateway can serve over TLS.
            </div>
          </div>
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle">HTTPS endpoint</div>
          <div className="gwCardSectionBody">
            {state.https_listen ? (
              <>
                <div className="gwTlsEndpointRow">
                  <code className="gwTlsEndpoint">{state.https_listen}</code>
                </div>
                <div className="gwNotice">
                  Configure your reverse proxy (or DNS + the gateway’s domain)
                  to point at this address. After a cert is active, browse to{" "}
                  <code>https://&lt;your-host&gt;{state.https_listen}</code>.
                </div>
              </>
            ) : (
              <div className="gwNotice">
                HTTPS listener is disabled. Set{" "}
                <code>CS_HTTPS_LISTEN=:8443</code> (or pass{" "}
                <code>--https=:8443</code>) and restart driverhost to enable.
              </div>
            )}
          </div>
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle">Active certificate</div>
          <div className="gwCardSectionBody">
            {cfg ? (
              <>
                <div className="gwTlsCertHero">
                  <span className={`gwLicenseStatusPill is-soon ${expiryClass}`}>
                    {expDays === null
                      ? "Active"
                      : expDays < 0
                      ? `Expired ${Math.abs(expDays)} day${
                          Math.abs(expDays) === 1 ? "" : "s"
                        } ago`
                      : `Expires in ${expDays} day${expDays === 1 ? "" : "s"}`}
                  </span>
                  <span className="gwLicenseHeroName">
                    {cfg.cert_subject || "(no subject)"}
                  </span>
                </div>
                <div className="gwLicenseDetailsGrid">
                  <div className="gwLicenseDetail">
                    <span className="gwLicenseDetailLabel">SAN / DNS names</span>
                    <span className="gwLicenseDetailValue is-text">
                      {(cfg.cert_dns_names || []).join(", ") || "—"}
                    </span>
                  </div>
                  <div className="gwLicenseDetail">
                    <span className="gwLicenseDetailLabel">Valid from</span>
                    <span className="gwLicenseDetailValue is-text">
                      {formatDate(cfg.cert_not_before)}
                    </span>
                  </div>
                  <div className="gwLicenseDetail">
                    <span className="gwLicenseDetailLabel">Valid until</span>
                    <span className="gwLicenseDetailValue is-text">
                      {formatDate(cfg.cert_not_after)}
                    </span>
                  </div>
                  <div className="gwLicenseDetail">
                    <span className="gwLicenseDetailLabel">SHA-256 fingerprint</span>
                    <span className="gwLicenseDetailValue">
                      {cfg.cert_fingerprint}
                      {cfg.cert_fingerprint && (
                        <CopyableText value={cfg.cert_fingerprint} />
                      )}
                    </span>
                  </div>
                  <div className="gwLicenseDetail">
                    <span className="gwLicenseDetailLabel">Last upload</span>
                    <span className="gwLicenseDetailValue is-text">
                      {formatDate(cfg.uploaded_at)}
                      {cfg.uploaded_by ? ` · by ${cfg.uploaded_by}` : ""}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="gwEmptyState">
                <div className="gwEmptyTitle">No certificate uploaded yet</div>
                <div className="gwEmptyBody">
                  HTTPS handshakes will fail until you upload one below.
                </div>
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">
              {cfg ? "Replace certificate" : "Upload certificate"}
            </div>
            <div className="gwCardSectionBody">
              <div className="gwNotice">
                Provide a PEM-encoded certificate chain and matching private
                key (PKCS#8 or PKCS#1). Either upload the file or paste the
                PEM contents directly. The private key is encrypted at rest
                with the gateway’s <code>CS_SECRET_KEY</code>. Active TLS
                handshakes start using the new cert immediately — no
                restart.
              </div>
              <form onSubmit={onUpload} className="gwTlsForm">
                <CertInputRow
                  label="Certificate (.pem / .crt)"
                  name="cert"
                  file={certFile}
                  text={certText}
                  onFile={setCertFile}
                  onText={setCertText}
                  mode={certMode}
                  onMode={setCertMode}
                />
                <CertInputRow
                  label="Private key (.pem / .key)"
                  name="key"
                  file={keyFile}
                  text={keyText}
                  onFile={setKeyFile}
                  onText={setKeyText}
                  mode={keyMode}
                  onMode={setKeyMode}
                />
                <div className="gwTlsFormActions">
                  <button
                    type="submit"
                    className="gwPrimaryBtn"
                    disabled={uploading || !hasInput}
                  >
                    {uploading ? "Validating + saving…" : "Upload certificate"}
                  </button>
                </div>
                {uploadError && (
                  <div className="gwAlert">
                    <div className="gwAlertTitle">Upload failed</div>
                    <div className="gwAlertBody">{uploadError}</div>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        <div className="gwCardSection">
          <div className="gwSectionTitle">Privacy</div>
          <div className="gwCardSectionBody">
            <div className="gwNotice">
              The certificate chain is stored in plaintext (it’s public
              information). The private key is encrypted with AES-256-GCM
              using the gateway’s <code>CS_SECRET_KEY</code> before being
              written to the database, and decrypted only in process memory
              during TLS handshakes. The key never leaves this gateway.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PlatformTLSPage;
